import React, { useState } from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { Dialog } from '@/components/common/Dialog';
import { rpc } from '@/api/client';
import { useI18n } from '@/context/I18nContext';
import { useToast } from '@/context/ToastContext';
import type { AnalysisRecord, DeleteAnalysisResponse } from '@/types/telemetry';

interface SessionRemovedAnalysisDialogProps {
  open: boolean;
  onClose: () => void;
  analysis: AnalysisRecord | null;
  onDeleted?: (analysisId: string) => void;
}

function getAnalysisTrackName(item: AnalysisRecord | null): string {
  if (!item) return '';
  const track = item.sector?.track || item.corner?.track || item.braking?.track || item.lapDiff?.track || item.aggregation?.track || item.aggregationDiff?.track;
  return track?.trackName || track?.trackId || '';
}

export const SessionRemovedAnalysisDialog: React.FC<SessionRemovedAnalysisDialogProps> = ({
  open,
  onClose,
  analysis,
  onDeleted,
}) => {
  const { t } = useI18n();
  const { showToast } = useToast();
  const [deleting, setDeleting] = useState(false);

  if (!analysis) return null;

  const trackName = getAnalysisTrackName(analysis);

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await rpc<DeleteAnalysisResponse>('DeleteAnalysis', { id: analysis.id });
      showToast(t('analysis.history.deleteSuccess'), 'success');
      onDeleted?.(analysis.id);
      onClose();
    } catch (err: any) {
      console.error('Failed to delete analysis:', err);
      showToast(err.message || 'Failed to delete analysis', 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('analysis.sessionRemoved.title')}
      footer={
        <div className="flex items-center justify-end gap-3 w-full">
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="px-4 py-2 text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-bg-hover rounded-lg transition-colors cursor-pointer"
          >
            {t('analysis.sessionRemoved.close')}
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-lg transition-colors cursor-pointer shadow-sm"
          >
            <Trash2 size={14} />
            {deleting ? t('session.deleting') || 'Deleting...' : t('analysis.sessionRemoved.deleteAnalysis')}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3 p-3.5 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-300">
          <AlertTriangle size={20} className="shrink-0 mt-0.5" />
          <div className="text-xs space-y-1">
            <p className="font-semibold text-amber-200">{t('analysis.sessionRemoved.title')}</p>
            <p className="text-amber-300/90 leading-relaxed">{t('analysis.sessionRemoved.description')}</p>
          </div>
        </div>

        <div className="p-3 bg-bg-surface/50 border border-border-subtle rounded-lg text-xs space-y-1.5">
          <div className="flex justify-between">
            <span className="text-text-muted">{t('analysis.history.table.name')}:</span>
            <span className="font-medium text-text-primary truncate max-w-[280px]">{analysis.name}</span>
          </div>
          {trackName && (
            <div className="flex justify-between">
              <span className="text-text-muted">{t('analysis.history.table.track')}:</span>
              <span className="font-medium text-text-primary">{trackName}</span>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};
